import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../components/Layout';
import { backtestApi } from '../services/api';
import { formatINR } from '../utils/formatters';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Flask, Warning, Sparkle, Ranking } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CAPITAL_DEFAULT = 100000;

export default function Backtest() {
  const [strategies, setStrategies] = useState([]);
  const [selected, setSelected] = useState('sma_crossover');
  const [symbol, setSymbol] = useState('RELIANCE');
  const [capital, setCapital] = useState(CAPITAL_DEFAULT);
  const [years, setYears] = useState(2);
  const [params, setParams] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    backtestApi.strategies()
      .then((r) => setStrategies(r.data.strategies || []))
      .catch((e) => console.error('strategies fetch failed', e));
  }, []);

  useEffect(() => {
    const s = strategies.find((x) => x.key === selected);
    if (s) setParams(s.params);
  }, [selected, strategies]);

  const run = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const payload = { symbol, strategy: selected, params, capital: Number(capital), years: Number(years) };
      const { data } = await backtestApi.run(payload);
      setResult(data);
    } catch (e) {
      console.error('backtest run failed', e);
      setError(e.response?.data?.detail || 'Backtest failed');
    } finally {
      setRunning(false);
    }
  }, [symbol, selected, params, capital, years]);

  const activeStrategy = strategies.find((s) => s.key === selected);

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-6" data-testid="backtest-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Flask size={32} weight="duotone" className="text-[#F59E0B]" />
            Strategy Backtester
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            Test any preset strategy against 2+ years of NSE daily data.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
          <ConfigPanel
            symbol={symbol} setSymbol={setSymbol}
            strategies={strategies}
            selected={selected} setSelected={setSelected}
            params={params} setParams={setParams}
            capital={capital} setCapital={setCapital}
            years={years} setYears={setYears}
            running={running} onRun={run}
            activeStrategy={activeStrategy}
          />
          <ResultsPanel result={result} error={error} running={running} />
        </div>
      </div>
    </Layout>
  );
}

function ConfigPanel({
  symbol, setSymbol, strategies, selected, setSelected,
  params, setParams, capital, setCapital, years, setYears, running, onRun, activeStrategy,
}) {
  return (
    <div className="space-y-4 border border-white/10 p-4 bg-[#0A0B0E]">
      <Field label="NSE Symbol">
        <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          className="bg-[#0A0B0E] border-white/20 text-white" data-testid="backtest-symbol" />
      </Field>
      <Field label="Strategy">
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
          className="w-full bg-[#0A0B0E] border border-white/20 text-white p-2 text-sm"
          data-testid="backtest-strategy">
          {strategies.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {activeStrategy && <p className="text-[11px] text-[#64748B] mt-1">{activeStrategy.description}</p>}
      </Field>

      <Field label="Parameters">
        <div className="space-y-2">
          {Object.entries(params).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[110px_1fr] gap-2 items-center">
              <span className="text-[11px] text-[#94A3B8] uppercase">{k}</span>
              <Input
                type="number"
                value={v}
                onChange={(e) => setParams((p) => ({ ...p, [k]: Number(e.target.value) }))}
                className="bg-[#0A0B0E] border-white/20 text-white h-8 text-xs"
                data-testid={`backtest-param-${k}`}
              />
            </div>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Capital (₹)">
          <Input type="number" value={capital} onChange={(e) => setCapital(Number(e.target.value))}
            className="bg-[#0A0B0E] border-white/20 text-white" data-testid="backtest-capital" />
        </Field>
        <Field label="Years">
          <Input type="number" min={1} max={5} value={years} onChange={(e) => setYears(Number(e.target.value))}
            className="bg-[#0A0B0E] border-white/20 text-white" data-testid="backtest-years" />
        </Field>
      </div>

      <Button onClick={onRun} disabled={running}
        className="w-full bg-[#F59E0B] hover:bg-orange-500 text-black font-semibold"
        data-testid="backtest-run-btn">
        {running ? 'Running…' : 'Run Backtest'}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ResultsPanel({ result, error, running }) {
  if (running) return <div className="text-[#94A3B8] text-sm p-6">Fetching {'>'}500 daily candles and running strategy…</div>;
  if (error) return (
    <div className="p-4 border border-[#EF4444]/30 bg-[#EF4444]/10 text-[#EF4444] text-sm">
      <Warning size={16} weight="fill" className="inline mr-2" /> {error}
    </div>
  );
  if (!result) return (
    <div className="text-[#64748B] text-sm p-6 border border-dashed border-white/10">
      Configure a strategy on the left and click Run Backtest.
    </div>
  );

  const m = result.metrics;
  return (
    <div className="space-y-5" data-testid="backtest-results">
      <MetricGrid metrics={m} period={result.period} />
      <EquityChart curve={result.equity_curve} />
      <AISuggestion result={result} />
      <Nifty50Ranker strategy={result.strategy} params={result.params_used} />
      <TradesTable trades={result.trades} />
    </div>
  );
}

function AISuggestion({ result }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);

  const ask = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuggestion(null);
    try {
      const { data } = await backtestApi.aiSuggest({
        symbol: result.symbol,
        strategy: result.strategy,
        params_used: result.params_used,
        metrics: result.metrics,
      });
      setSuggestion(data.suggestion);
    } catch (e) {
      console.error('ai suggest failed', e);
      setError(e.response?.data?.detail || 'AI suggestion failed');
    } finally {
      setLoading(false);
    }
  }, [result]);

  return (
    <div className="border border-[#A855F7]/30 bg-[#A855F7]/5 p-4" data-testid="ai-suggest-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" className="text-[#A855F7]" />
          <h3 className="text-sm font-bold text-white">AI Strategy Suggestion</h3>
        </div>
        <Button
          onClick={ask}
          disabled={loading}
          className="bg-[#A855F7] hover:bg-purple-600 text-white text-xs h-8"
          data-testid="ai-suggest-btn"
        >
          {loading ? 'Analyzing…' : suggestion ? 'Re-analyze' : 'Ask GPT-5.2'}
        </Button>
      </div>
      {error && <p className="text-[#EF4444] text-xs">{error}</p>}
      {suggestion && (
        <div className="text-sm text-[#94A3B8] leading-relaxed prose-invert" data-testid="ai-suggestion-content">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-2">{children}</p>,
              strong: ({ children }) => <strong className="text-white">{children}</strong>,
              ul: ({ children }) => <ul className="list-none space-y-1 mb-2">{children}</ul>,
              li: ({ children }) => (
                <li className="flex items-start gap-2">
                  <span className="text-[#A855F7] mt-0.5">▸</span>
                  <span>{children}</span>
                </li>
              ),
              code: ({ children }) => <code className="bg-[#0A0B0E] px-1 text-[#00FF66] font-mono text-xs">{children}</code>,
            }}
          >{suggestion}</ReactMarkdown>
        </div>
      )}
      {!suggestion && !loading && !error && (
        <p className="text-xs text-[#64748B]">
          Click to get 3 concrete parameter tweaks that would likely improve Sharpe.
        </p>
      )}
    </div>
  );
}

function Nifty50Ranker({ strategy, params }) {
  const [loading, setLoading] = useState(false);
  const [ranked, setRanked] = useState(null);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRanked(null);
    try {
      const { data } = await backtestApi.runNifty50({
        strategy, params, capital: 100000, years: 2,
      });
      setRanked(data);
    } catch (e) {
      console.error('nifty50 backtest failed', e);
      setError(e.response?.data?.detail || 'Batch backtest failed');
    } finally {
      setLoading(false);
    }
  }, [strategy, params]);

  return (
    <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4" data-testid="nifty50-ranker-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ranking size={20} weight="fill" className="text-[#F59E0B]" />
          <h3 className="text-sm font-bold text-white">Rank Across Nifty 50</h3>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          className="bg-[#F59E0B] hover:bg-orange-500 text-black font-semibold text-xs h-8"
          data-testid="nifty50-run-btn"
        >
          {loading ? 'Running 50 backtests…' : ranked ? 'Re-run' : 'Rank Nifty 50 by Sharpe'}
        </Button>
      </div>
      {error && <p className="text-[#EF4444] text-xs">{error}</p>}
      {loading && (
        <p className="text-xs text-[#94A3B8]">
          Running the current strategy across all 50 Nifty stocks. This takes ~60-90s…
        </p>
      )}
      {ranked && <RankTable ranked={ranked} />}
      {!ranked && !loading && !error && (
        <p className="text-xs text-[#64748B]">
          Discover which Nifty 50 stocks the current strategy performs best on.
        </p>
      )}
    </div>
  );
}

function RankTable({ ranked }) {
  return (
    <div>
      <p className="text-[10px] text-[#64748B] mb-2">
        Ran {ranked.total_symbols} • Succeeded {ranked.successful} • Failed {ranked.failed}
      </p>
      <div className="border border-white/10 overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-xs" data-testid="nifty50-rank-table">
          <thead className="bg-white/5 text-[#94A3B8] sticky top-0">
            <tr>
              <th className="text-left py-2 px-3">#</th>
              <th className="text-left py-2 px-3">Symbol</th>
              <th className="text-right py-2 px-3">Sharpe</th>
              <th className="text-right py-2 px-3">Return %</th>
              <th className="text-right py-2 px-3">B&H %</th>
              <th className="text-right py-2 px-3">Outperf %</th>
              <th className="text-right py-2 px-3">Win %</th>
              <th className="text-right py-2 px-3">Trades</th>
              <th className="text-right py-2 px-3">Max DD %</th>
            </tr>
          </thead>
          <tbody>
            {ranked.ranked.map((r, i) => (
              <tr key={r.symbol} className="border-t border-white/5">
                <td className="py-1.5 px-3 text-[#64748B] font-mono">{i + 1}</td>
                <td className="py-1.5 px-3 text-white font-mono">{r.symbol.replace('.NS', '')}</td>
                <td className={`py-1.5 px-3 text-right font-mono ${r.sharpe_ratio > 0.5 ? 'text-[#00FF66]' : r.sharpe_ratio < 0 ? 'text-[#EF4444]' : 'text-white'}`}>
                  {r.sharpe_ratio}
                </td>
                <td className={`py-1.5 px-3 text-right font-mono ${r.total_return_percent >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {r.total_return_percent}%
                </td>
                <td className="py-1.5 px-3 text-right font-mono text-[#94A3B8]">{r.buy_and_hold_percent}%</td>
                <td className={`py-1.5 px-3 text-right font-mono ${r.outperformance_percent >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {r.outperformance_percent}%
                </td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{r.win_rate_percent}%</td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{r.total_trades}</td>
                <td className="py-1.5 px-3 text-right font-mono text-[#EF4444]">{r.max_drawdown_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricGrid({ metrics, period }) {
  const returnColor = metrics.total_return_percent >= 0 ? '#10B981' : '#EF4444';
  const outColor = metrics.outperformance_percent >= 0 ? '#10B981' : '#EF4444';
  const stats = [
    { label: 'Total Return', value: `${metrics.total_return_percent}%`, color: returnColor, testid: 'metric-return' },
    { label: 'Buy & Hold', value: `${metrics.buy_and_hold_percent}%`, color: '#94A3B8', testid: 'metric-buyhold' },
    { label: 'Outperformance', value: `${metrics.outperformance_percent}%`, color: outColor, testid: 'metric-outperf' },
    { label: 'Total Trades', value: metrics.total_trades, color: '#FFFFFF', testid: 'metric-trades' },
    { label: 'Win Rate', value: `${metrics.win_rate_percent}%`, color: '#FFFFFF', testid: 'metric-winrate' },
    { label: 'Max Drawdown', value: `${metrics.max_drawdown_percent}%`, color: '#EF4444', testid: 'metric-maxdd' },
    { label: 'Sharpe Ratio', value: metrics.sharpe_ratio, color: '#F59E0B', testid: 'metric-sharpe' },
    { label: 'Final Value', value: formatINR(metrics.final_value), color: '#10B981', testid: 'metric-final' },
  ];
  return (
    <>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
        Period: {period.start} → {period.end} ({period.bars} bars)
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="p-3 border border-white/10 bg-white/[0.02]" data-testid={s.testid}>
            <div className="text-[10px] text-[#64748B] uppercase">{s.label}</div>
            <div className="text-lg font-mono font-bold mt-1" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function EquityChart({ curve }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Equity Curve</p>
      <div className="border border-white/10 p-3 bg-white/[0.02]" style={{ height: 280, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
          <LineChart data={curve}>
            <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={10} tick={{ fill: '#64748B' }} />
            <YAxis stroke="#64748B" fontSize={10} tick={{ fill: '#64748B' }} />
            <Tooltip contentStyle={{ background: '#0A0B0E', border: '1px solid #1F2937' }} />
            <Line dataKey="equity" stroke="#10B981" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TradesTable({ trades }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Trade Log ({trades.length})</p>
      <div className="border border-white/10 overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-xs" data-testid="backtest-trades-table">
          <thead className="bg-white/5 text-[#94A3B8] sticky top-0">
            <tr>
              <th className="text-left py-2 px-3">Entry Date</th>
              <th className="text-right py-2 px-3">Entry ₹</th>
              <th className="text-left py-2 px-3">Exit Date</th>
              <th className="text-right py-2 px-3">Exit ₹</th>
              <th className="text-right py-2 px-3">Days</th>
              <th className="text-right py-2 px-3">P&L</th>
              <th className="text-right py-2 px-3">P&L %</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => (
              <tr key={`${t.entry_date}-${t.exit_date}`} className="border-t border-white/5">
                <td className="py-1.5 px-3 text-white">{t.entry_date}</td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{t.entry_price}</td>
                <td className="py-1.5 px-3 text-[#94A3B8]">{t.exit_date}</td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{t.exit_price}</td>
                <td className="py-1.5 px-3 text-right font-mono text-[#64748B]">{t.bars_held}</td>
                <td className={`py-1.5 px-3 text-right font-mono ${t.pnl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {t.pnl >= 0 ? '+' : ''}{t.pnl}
                </td>
                <td className={`py-1.5 px-3 text-right font-mono ${t.pnl_percent >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {t.pnl_percent >= 0 ? '+' : ''}{t.pnl_percent}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
